'use client';

import {ImageWithFallback} from '@purpose/ui/components/ImageWithFallback';
import {useResponsive} from '@purpose/ui/hooks/useResponsive';

interface SimpleRoundedCardProps {
  width?: number | string;
  image: string | null;
  title?: string;
  small?: boolean;
  onClick?: () => void;
  description?: string;
  subtitle?: string;
  categoryDesign?: boolean;
  priority?: boolean;
  overtitle?: React.JSX.Element;
}

const SimpleRoundedCard = ({
  width,
  image,
  small,
  title,
  onClick,
  subtitle,
  categoryDesign,
  description,
  priority = false,
  overtitle = <></>
}: SimpleRoundedCardProps) => {
  const {isXs, isSm, isMd, isLg, isMobile} = useResponsive();

  const imageHeight = () => {
    if (isXs) {
      return small ? 153 : 247;
    } else if (isSm) {
      return small ? 90 : 154;
    } else if (isMd) {
      return small ? 138 : 127;
    } else if (isLg) {
      return small ? 114 : 211;
    } else {
      return small ? 136 : 205;
    }
  };

  return (
    <div
      className="card-container"
      style={{
        width: small
          ? '100%'
          : typeof width === 'string'
            ? width
            : `${width || 230}px`,
        borderRadius: '16px',
        backgroundColor: categoryDesign ? '#fafafaff' : 'transparent',
        border: categoryDesign ? 'none' : '1px solid #eeeeeeff',
        cursor: 'pointer',
        display: 'flex',
        gap: '4px',
        overflow: 'hidden',
        position: 'relative',
        justifyContent: small ? 'center' : 'space-between',
        alignItems: small ? 'center' : 'stretch',
        height: '100%',
        padding: small ? (isSm ? '8px' : '16px') : '12px 16px'
      }}
    >
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          alignItems: small ? 'center' : 'stretch',
          justifyContent: small ? 'center' : 'flex-start'
        }}
      >
        <div
          className="mx-auto"
          style={{
            flex: 1,
            display: 'flex',
            gap: '16px',
            flexDirection: 'column',
            alignItems: small ? 'center' : 'stretch',
            justifyContent: small ? 'center' : 'flex-start'
          }}
        >
          <ImageWithFallback
            {...(onClick && {onClick})}
            className="card-image"
            width={isXs ? '100%' : imageHeight()}
            src={image}
            fallbackWrapperClassName="rounded-full"
            alt={title || 'card image'}
            priority={priority}
            loading={priority ? 'eager' : 'lazy'}
            placeholder="empty"
            quality={85}
            sizes={`${imageHeight()}px`}
            containerStyle={{
              margin: small ? '0 auto' : 'auto',
              aspectRatio: '1 / 1'
            }}
            style={{
              borderRadius: '50%',
              margin: small ? '0 auto' : 'auto',
              aspectRatio: '1 / 1',
              height: 'auto',
              width: isXs ? '100%' : imageHeight(),
              objectFit: 'cover'
            }}
            iconStyles={{
              width: isMobile ? 48 : 124,
              height: isMobile ? 48 : 124
            }}
          />
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              alignItems: small ? 'center' : 'stretch',
              textAlign: small ? 'center' : 'left',
              ...(isMobile && {justifyContent: 'center'})
            }}
          >
            {overtitle}
            {title && (
              <h3
                {...(onClick && {onClick})}
                style={{
                  fontWeight: 600,
                  margin: 0,
                  fontSize: small ? (isSm ? '14px' : '16px') : '16px',
                  lineHeight: '20px',
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textAlign: small ? 'center' : 'left'
                }}
              >
                {title}
              </h3>
            )}
            {categoryDesign && description && (
              <p
                style={{
                  margin: 0,
                  fontSize: '14px',
                  color: '#666666ff',
                  textAlign: small ? 'center' : 'left'
                }}
              >
                {description}
              </p>
            )}
            {categoryDesign && subtitle && (
              <p
                style={{
                  margin: '0 0 8px 0',
                  fontSize: '14px',
                  fontWeight: 600,
                  textAlign: small ? 'center' : 'left'
                }}
              >
                {subtitle}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export {SimpleRoundedCard};
export type {SimpleRoundedCardProps};
